import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { flowExecutions } from '../../queries/flow-executions';
import type { FlowRunStatus } from '../../types/flow-execution.types';
import ExecutionCard from './ExecutionCard';

interface Props {
  flowId: string;
  page: number;
  status: FlowRunStatus | 'all';
  selectedExecId: string | null;
  onSelect: (execId: string) => void;
  onPageChange: (page: number) => void;
  onStatusChange: (status: FlowRunStatus | 'all') => void;
}

const PAGE_LIMIT = 25;

function ExecutionsList({
  flowId,
  page,
  status,
  selectedExecId,
  onSelect,
  onPageChange,
  onStatusChange,
}: Props) {
  const { data, isLoading, isError } = useQuery(
    flowExecutions.runs({
      flowId,
      page,
      limit: PAGE_LIMIT,
      status: status === 'all' ? undefined : status,
    }),
  );
  return (
    <Stack sx={{ height: '100%' }}>
      <Stack direction='row' spacing={1} sx={{ mb: 1.5 }}>
        <FormControl size='small' fullWidth>
          <InputLabel id='exec-status-filter-label'>Status</InputLabel>
          <Select
            labelId='exec-status-filter-label'
            label='Status'
            value={status}
            onChange={e =>
              onStatusChange(e.target.value as FlowRunStatus | 'all')
            }
          >
            <MenuItem value='all'>All</MenuItem>
            <MenuItem value='ok'>Success</MenuItem>
            <MenuItem value='error'>Failed</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {isError && (
          <Typography color='error' variant='body2' sx={{ py: 2 }}>
            Failed to load executions.
          </Typography>
        )}
        {!isLoading && !isError && data && data.data.length === 0 && (
          <Typography
            color='text.secondary'
            variant='body2'
            sx={{ py: 4, textAlign: 'center' }}
          >
            No executions yet.
          </Typography>
        )}
        {data && data.data.length > 0 && (
          <Stack spacing={1}>
            {data.data.map(run => (
              <ExecutionCard
                key={run.execId}
                run={run}
                selected={selectedExecId === run.execId}
                onSelect={onSelect}
              />
            ))}
          </Stack>
        )}
      </Box>
      {data && data.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
          <Pagination
            size='small'
            count={data.totalPages}
            page={page}
            onChange={(_, value) => onPageChange(value)}
          />
        </Box>
      )}
    </Stack>
  );
}

export default ExecutionsList;
